// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * F12: unified email + passcode onboarding form.
 *
 * Replaces the pre-F12 `AuthGate` / `UnlockForm` / `GetStartedForm`
 * split in home.tsx. The reducer in `auth-flow.ts` owns the state
 * machine; this component just dispatches events and renders the
 * current state.
 *
 * Critical property: when the server says `match` but the local
 * device has no seed, this component routes to the "restore this
 * device" branch — NEVER to the "set up new account" branch. That
 * routing lives in the reducer, not here; see
 * `tests/auth-flow.test.ts` for the regression guards.
 *
 * COB-646 (decision Q11): the auth-flow state is held by `useReducer`
 * and written ONLY through `dispatch`. There is no `setState` for it
 * to write — that is deliberate, not incidental. Before COB-646 this
 * component performed 18 of the transitions with direct object
 * literals, so the exhaustive pair table in
 * `tests/auth-flow-pairs.test.ts` guarded a machine the shipping flow
 * only partly went through. `tests/onboarding-dispatch.test.tsx`
 * pins every dispatch site: the exact event, and the LIVE state the
 * reducer saw. The only `useState` calls left in the auth-flow path
 * are non-transition view state, each labelled where it is declared.
 */

import { useCallback, useReducer, useRef, useState } from 'preact/hooks';
import { useSession } from '../session-context.js';
import { ApiError, type PasscodeCheckStatus } from '../api.js';
import { createKeyAgentClient } from '../key-agent-loader.js';
import { PasswordManagerUsername } from '../components/password-manager-username.js';
import {
  initialAuthFlowState,
  reduceAuthFlow,
  validateEmail,
  validatePasscode,
  PASSCODE_MIN_LENGTH,
} from '../auth-flow.js';

/** COB-501: how many of the 24 words the backup check asks for.
 *
 *  Everything downstream is driven by this constant — `pickIndices`, the input
 *  array, and the render — so raising it to 6 is a one-line product change.
 *  Deliberately still 3: full 24-word re-entry was rejected (the entry grid
 *  supports paste, so its extra assurance doesn't survive a digital copy,
 *  while the drop-off at a money-adjacent moment is real). Exported for tests. */
export const VERIFY_COUNT = 3;

/** COB-501: wrong attempts allowed before the check sends the user back to the
 *  phrase itself. Unlimited retries against a fixed set of positions made this
 *  an attention check rather than proof of a backup; the cap plus the per-
 *  attempt re-roll closes both the "guess the same three slots" and the lazy-
 *  retry paths. Reset on every entry into the verify step. Exported for tests. */
export const MAX_VERIFY_ATTEMPTS = 3;

const WORD_COUNT = 24;

/** COB-502: the `passwordrules` value for every `new-password` field, built
 *  from the one client-side minimum so the two can never drift. Safari reads
 *  it when it generates a strong passcode. */
export const PASSCODE_RULES = `minlength: ${PASSCODE_MIN_LENGTH}`;

/** The passcode field, shared by all four onboarding surfaces.
 *
 *  COB-502 deliberately leaves it with no `disabled` and no `onKeyDown`: every
 *  caller now sits inside a `<form>`, so Enter submits natively, and disabling
 *  a passcode input mid-submit is precisely what makes Safari drop its
 *  AutoFill affordance. Both were removed rather than left unused so the
 *  property is structural. */
function PasscodeInput({
  id,
  name,
  value,
  onInput,
  autocomplete,
  passwordrules,
}: {
  /** DOM id — required: it is the `label for` target and, with `name`, the
   *  only stable classifier signal a password manager has on this field
   *  (COB-502). */
  id: string;
  name: string;
  value: string;
  onInput: (v: string) => void;
  autocomplete?: string;
  passwordrules?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div class="home-auth-passcode">
      <input
        id={id}
        name={name}
        class="home-auth-input"
        type={visible ? 'text' : 'password'}
        value={value}
        onInput={(e) => onInput((e.target as HTMLInputElement).value)}
        // COB-502: a fill that lands without an observed `input` event would
        // otherwise be blanked by the next controlled re-render (and leave the
        // submit button disabled). Belt-and-braces — current Safari/Chrome do
        // fire `input` on autofill.
        onChange={(e) => onInput((e.target as HTMLInputElement).value)}
        autocomplete={autocomplete}
        passwordrules={passwordrules}
      />
      <button
        type="button"
        class="home-auth-toggle-vis"
        onClick={() => setVisible(!visible)}
        aria-label={visible ? 'Hide passcode' : 'Show passcode'}
        aria-pressed={visible}
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

/** A uniform integer in `[0, max)` drawn from the platform CSPRNG.
 *
 *  COB-501: `Math.random()` is not a CSPRNG, and reducing a raw draw with
 *  `% max` is biased whenever `max` is not a power of two (24 is not). This
 *  rejection-samples over the smallest covering bit mask instead, which is
 *  exactly uniform; for `max = 24` it accepts 24/32 of draws. The iteration
 *  bound is a paranoia rail against a broken RNG — with a working one the
 *  probability of reaching it is (8/32)^64 ≈ 2^-128 — and throws rather than
 *  falling back to a biased draw. */
function randomIndex(max: number): number {
  if (max < 1) throw new Error('randomIndex: max must be at least 1');
  const mask = 2 ** Math.ceil(Math.log2(max)) - 1;
  const buf = new Uint32Array(1);
  for (let i = 0; i < 64; i++) {
    crypto.getRandomValues(buf);
    const candidate = buf[0] & mask;
    if (candidate < max) return candidate;
  }
  throw new Error('randomIndex: rejection sampling exhausted');
}

/** `count` distinct word positions in `[0, max)`, ascending. Exported for the
 *  COB-501 tests (in-range, distinct, and CSPRNG-sourced). */
export function pickIndices(count: number, max: number): number[] {
  if (count > max) throw new Error('pickIndices: count exceeds the word count');
  const indices: number[] = [];
  while (indices.length < count) {
    const i = randomIndex(max);
    if (!indices.includes(i)) indices.push(i);
  }
  return indices.sort((a, b) => a - b);
}

/** COB-501: the retry message, naming the re-roll and what is left. Exported so
 *  the tests assert the exact copy. */
export function verifyFailureMessage(attemptsLeft: number): string {
  const attempts = attemptsLeft === 1 ? 'attempt' : 'attempts';
  return (
    'One or more words are incorrect. We’ve picked different words — ' +
    `${attemptsLeft} ${attempts} left before you review your phrase again.`
  );
}

/** COB-501: what the backup step says when the attempt cap sent the user back.
 *  The point is to re-read the words, so the copy is an instruction rather than
 *  an accusation. Exported for tests. */
export const VERIFY_EXHAUSTED_NOTICE =
  'That didn’t match. Check your written copy against all 24 words below, ' +
  'then run the check again.';

export function Onboarding(
  { onBack, initialEmail }: { onBack?: () => void; initialEmail?: string } = {},
) {
  const session = useSession();
  // COB-646: the auth-flow state machine. `dispatch` is the ONLY writer —
  // `reduceAuthFlow` decides every transition, including the seed-backup
  // guards, so a transition that is not an arm in `auth-flow.ts` cannot
  // happen here.
  const [state, dispatch] = useReducer(reduceAuthFlow, initialAuthFlowState);
  // `initialEmail` prefills the field when the chooser bounces a
  // web3auth email-collision here (0034); applied on mount only.
  // Form-field view state, not flow transitions.
  const [email, setEmail] = useState(initialEmail ?? '');
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);

  // Hold the passcode across the SeedSetupFlow handoff. Using a ref so React
  // never re-renders with plaintext in state, and so the value survives the
  // async seed-backup dance. Cleared on flow completion and on explicit reset.
  // COB-646 (doc 09 §7 Q5): the EMAIL half is no longer read from here —
  // every panel below takes `state.email`, so the reducer's email is
  // load-bearing in production rather than write-only. The passcode cannot
  // move into the state machine for the reason above, so the ref stays.
  const credsRef = useRef<{ email: string; passcode: string } | null>(null);

  // COB-646: a view step inside `checking`, not a flow state. The re-enter-
  // passcode screen sits between the server's `no_account` answer and the
  // `LookupNoAccount` dispatch, so the machine is still in `checking` while
  // it renders — which is exactly right: nothing has been decided yet.
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const reset = useCallback(() => {
    dispatch({ type: 'Reset' });
    setPasscode('');
    setPendingConfirm(false);
    credsRef.current = null;
    setBusy(false);
  }, []);

  async function handleInitialSubmit() {
    const emailErr = validateEmail(email);
    const pcErr = validatePasscode(passcode);
    // A submit always restarts the machine: `error` is terminal but for
    // `Reset`, so without this a second attempt from the error panel could
    // not leave it. `Reset` from `initial` is a no-op returning the shared
    // const, so the happy path is unaffected.
    dispatch({ type: 'Reset' });
    dispatch({ type: 'SubmitCredentials', email });
    // Pre-flight rejections are the lookup failing before it is made, so they
    // travel as `LookupError` from `checking` — the same arm a failed
    // /auth/passcode-check takes. See auth-flow.ts `validateEmail` for why the
    // guard itself stays here (doc 09 §7 Q6).
    if (emailErr) {
      dispatch({ type: 'LookupError', message: emailErr });
      return;
    }
    if (pcErr) {
      dispatch({ type: 'LookupError', message: pcErr });
      return;
    }
    setBusy(true);
    try {
      // Annotated explicitly so the exhaustiveness below is anchored to
      // `api.ts`'s declared alphabet, not to whatever the session happens to
      // be inferred as returning (COB-646).
      const status: PasscodeCheckStatus = await session.passcodeCheck(email, passcode);
      credsRef.current = { email, passcode };
      switch (status) {
        case 'no_account':
          setPendingConfirm(true);
          break;
        case 'match': {
          // Load the agent to check whether a local seed exists. Do
          // this WITHOUT calling verify yet — we need to branch on
          // hasSeed() first.
          const hasLocalSeed = await checkLocalSeedFor(email, session.bitcoinNetwork);
          dispatch(
            hasLocalSeed
              ? { type: 'LookupMatchWithSeed' }
              : { type: 'LookupMatchNoSeed' },
          );
          break;
        }
        case 'mismatch':
          dispatch({ type: 'LookupMismatch' });
          break;
        case 'web3auth':
          // 0034: this email belongs to a passwordless social-login account —
          // there is no passcode to check. The reducer owns both the routing
          // and the steering copy (COB-646).
          dispatch({ type: 'LookupWeb3AuthAccount' });
          break;
        default:
          // COB-646: exhaustive over `PasscodeCheckStatus`. A fifth status
          // added to `api.ts` fails `npm run typecheck` here rather than
          // silently taking the branch below.
          assertStatusHandled(status);
          // Runtime belt-and-braces for a server that sends a status this
          // build does not know: treat it as a mismatch, never as
          // `no_account`, so an unknown status can never reach seed
          // generation. Same landing state as before COB-646.
          dispatch({ type: 'LookupMismatch' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Lookup failed';
      dispatch({ type: 'LookupError', message: msg });
    } finally {
      setBusy(false);
    }
  }

  /** Submit a mnemonic for restore. Shared by `restorePrompt` and the retry
   *  inside `restoreWrongSeed`, so both take the same reducer arms. */
  function submitMnemonicForRestore(mnemonic: string) {
    const creds = credsRef.current!;
    dispatch({ type: 'SubmitMnemonic' });
    runRestore(session, creds.email, mnemonic, creds.passcode)
      .then(() => dispatch({ type: 'RestoreOk' }))
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          dispatch({ type: 'RestoreFailedUnknownUser' });
        } else {
          dispatch({
            type: 'RestoreError',
            message: e instanceof Error ? e.message : 'Restore failed',
          });
        }
      });
  }

  return (
    <div>
      {!pendingConfirm &&
        (state.kind === 'initial' || state.kind === 'checking' || state.kind === 'error') ? (
        <InitialForm
          email={email}
          setEmail={setEmail}
          passcode={passcode}
          setPasscode={setPasscode}
          onSubmit={handleInitialSubmit}
          onBack={onBack}
          busy={busy || state.kind === 'checking'}
          errorMessage={state.kind === 'error' ? state.message : null}
        />
      ) : null}

      {pendingConfirm && credsRef.current && (
        <ConfirmPasscodeStep
          email={credsRef.current.email}
          passcode={credsRef.current.passcode}
          onConfirmed={() => {
            setPendingConfirm(false);
            dispatch({ type: 'LookupNoAccount' });
          }}
          onBack={reset}
        />
      )}

      {state.kind === 'seedSetupBlocking' && credsRef.current && (
        <SeedSetupBlocking
          email={state.email}
          passcode={credsRef.current.passcode}
          onDone={() => dispatch({ type: 'ConfirmSeedBackup' })}
          onBack={reset}
        />
      )}

      {state.kind === 'unlocking' && credsRef.current && (
        <UnlockRunner
          email={state.email}
          passcode={credsRef.current.passcode}
          onUnlockFailedUnknownUser={() =>
            dispatch({ type: 'UnlockFailedUnknownUser' })
          }
          onError={(msg) => dispatch({ type: 'UnlockError', message: msg })}
        />
      )}

      {state.kind === 'restorePrompt' && credsRef.current && (
        <RestorePrompt
          email={state.email}
          onSubmitSeed={submitMnemonicForRestore}
          onBack={reset}
        />
      )}

      {state.kind === 'restoring' && (
        <div class="home-auth">
          <p class="home-auth-subtitle">Restoring your device...</p>
        </div>
      )}

      {state.kind === 'restoreWrongSeed' && credsRef.current && (
        <RestoreWrongSeed
          email={state.email}
          onRetrySeed={submitMnemonicForRestore}
          onBack={reset}
        />
      )}

      {state.kind === 'mismatchOptions' && credsRef.current && (
        <MismatchOptions
          email={state.email}
          onRetry={reset}
          onRecover={() => dispatch({ type: 'ChooseRecover' })}
        />
      )}

      {state.kind === 'recoverWithSeed' && credsRef.current && (
        <RecoverWithSeed
          email={state.email}
          onSubmit={(mnemonic, newPasscode) => {
            const recoverEmail = credsRef.current!.email;
            dispatch({ type: 'SubmitReset' });
            session
              .resetPasscodeWithSeed(recoverEmail, mnemonic, newPasscode)
              .then(() => dispatch({ type: 'ResetOk' }))
              .catch((e) =>
                dispatch({
                  type: 'ResetError',
                  message: e instanceof Error ? e.message : 'Reset failed',
                }),
              );
          }}
          onBack={reset}
        />
      )}

      {state.kind === 'resetting' && (
        <div class="home-auth">
          <p class="home-auth-subtitle">Resetting passcode...</p>
        </div>
      )}
    </div>
  );
}

// ── hasSeed probe (without committing an agent to session state) ────

async function checkLocalSeedFor(email: string, network: string | null): Promise<boolean> {
  // Short-lived Worker: we only need hasSeed() which is session-free but
  // requires the email-derived master-key lookup. Terminated before we
  // return so the probe leaves no Worker running.
  const client = createKeyAgentClient(toNet(network));
  try {
    await client.setEmail(email);
    return await client.hasSeed();
  } finally {
    client.terminate();
  }
}

function toNet(n: string | null): 'mainnet' | 'testnet' | 'signet' | 'regtest' {
  switch (n) {
    case 'bitcoin':
      return 'mainnet';
    case 'signet':
      return 'signet';
    case 'testnet':
      return 'testnet';
    default:
      return 'regtest';
  }
}

async function runRestore(
  session: ReturnType<typeof useSession>,
  email: string,
  mnemonic: string,
  passcode: string,
): Promise<void> {
  const normalised = mnemonic.trim().toLowerCase().replace(/\s+/g, ' ');
  await session.restoreAndVerify(email, normalised, passcode);
}

/** COB-646: compile-time proof that `handleInitialSubmit` has a branch for
 *  every `PasscodeCheckStatus`. `api.ts` currently declares four; adding a
 *  fifth without a `case` here makes the argument no longer `never` and fails
 *  `npm run typecheck`. Deliberately a no-op at runtime — the unknown-status
 *  fallback next to the call site is what keeps the flow fail-closed. */
function assertStatusHandled(_status: never): void {
  /* type-level only */
}

// ── Confirm passcode (new-account flow) ─────────────────────────────

function ConfirmPasscodeStep({
  email,
  passcode,
  onConfirmed,
  onBack,
}: {
  email: string;
  passcode: string;
  onConfirmed: () => void;
  onBack: () => void;
}) {
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleConfirm() {
    if (confirm !== passcode) {
      setError('Passcodes do not match. Try again.');
      return;
    }
    onConfirmed();
  }

  return (
    // COB-502: this is where the credential is first saved, so it is a real
    // form (submit event) carrying a username companion — a save with no
    // username field lands in the keychain blank and poisons the origin.
    <form
      class="home-auth"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleConfirm();
      }}
    >
      <h2 class="home-auth-title">Confirm your passcode</h2>
      <p class="home-auth-subtitle">
        No account found for <strong>{email}</strong> — we'll create one.
        Re-enter your passcode to make sure it's right. You'll need it
        every time you sign in, and there's no reset without your seed phrase.
      </p>
      <PasswordManagerUsername id="cobault-create-username" email={email} />
      <div class="home-auth-field">
        <label class="home-auth-label" for="cobault-create-passcode-confirm">
          Re-enter passcode
        </label>
        <PasscodeInput
          id="cobault-create-passcode-confirm"
          name="new-passcode-confirm"
          value={confirm}
          onInput={setConfirm}
          autocomplete="new-password"
          passwordrules={PASSCODE_RULES}
        />
      </div>
      {error && <p class="home-auth-error">{error}</p>}
      <div class="form-actions">
        <button type="button" class="btn btn-secondary btn-block" onClick={onBack}>
          Back
        </button>
        <button type="submit" class="btn btn-primary btn-block" disabled={!confirm}>
          Create account
        </button>
      </div>
    </form>
  );
}

// ── Initial form ────────────────────────────────────────────────────

interface InitialFormProps {
  email: string;
  setEmail: (v: string) => void;
  passcode: string;
  setPasscode: (v: string) => void;
  onSubmit: () => void;
  onBack?: () => void;
  busy: boolean;
  errorMessage: string | null;
}

function InitialForm({
  email,
  setEmail,
  passcode,
  setPasscode,
  onSubmit,
  onBack,
  busy,
  errorMessage,
}: InitialFormProps) {
  return (
    // COB-502: a real <form> with a real submit button. Without one no submit
    // event ever fires, so Safari's save path is dead and its fill path runs
    // in heuristic mode — the ranked #1 cause of the inconsistent iCloud
    // Keychain behaviour on this panel. Native submit also supplies the
    // Enter-to-continue the passcode field used to hand-roll.
    // `noValidate` keeps `validateEmail`/`validatePasscode` the single source
    // of error copy (the native bubble would pre-empt them).
    <form
      class="home-auth"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        // Double-submit guard: the button is disabled while busy, but Enter
        // reaches the form regardless of the button's state.
        if (busy) return;
        onSubmit();
      }}
    >
      <h2 class="home-auth-title">Sign in or create account</h2>
      <p class="home-auth-subtitle">
        Enter your email and passcode. If you already have an account on any
        device we'll recognise it; otherwise we'll walk you through creating one.
      </p>
      <div class="home-auth-field">
        <label class="home-auth-label" for="cobault-login-email">
          Email
        </label>
        {/* `autocomplete="username"` (not `email`): this IS the username half
            of the saved credential. `type`/`inputMode` stay email so the
            keyboard and validation are unchanged. */}
        <input
          id="cobault-login-email"
          name="email"
          class="home-auth-input"
          type="email"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
          autocomplete="username"
          inputMode="email"
        />
      </div>
      <div class="home-auth-field">
        <label class="home-auth-label" for="cobault-login-passcode">
          Passcode (at least {PASSCODE_MIN_LENGTH} characters)
        </label>
        <PasscodeInput
          id="cobault-login-passcode"
          name="passcode"
          value={passcode}
          onInput={setPasscode}
          autocomplete="current-password"
        />
      </div>
      {errorMessage && <p class="home-auth-error">{errorMessage}</p>}
      {/* Only the button goes disabled during the ~500ms Argon2id derive —
          disabling the inputs mid-submit is what makes Safari drop the
          field's AutoFill affordance (COB-502). */}
      <button
        type="submit"
        class="btn btn-primary btn-block"
        disabled={busy || !email || !passcode}
      >
        {busy ? 'Checking...' : 'Continue'}
      </button>
      {onBack && (
        <button
          class="btn btn-link login-chooser-back"
          type="button"
          onClick={onBack}
          disabled={busy}
        >
          ← Use a different login method
        </button>
      )}
    </form>
  );
}

// ── Seed setup (new account) ────────────────────────────────────────

type SeedStep = 'generating' | 'backup' | 'verify' | 'registering';

/** COB-272 (FIND-009): the seed-loss acknowledgement checkbox label. The
 *  passive warning copy alone proved too easy to click through — continuing
 *  now requires an affirmative attestation (mirrors the abandon-flow
 *  checkbox). Exported for the backup-step tests. */
export const SEED_LOSS_ACK_LABEL =
  'I understand that if I lose my seed phrase I lose access to my account, ' +
  'and that no one — including Cobault — can recover it for me.';

/** The backup step of the new-account seed setup: show the 24 words, then
 *  gate "I've written them down" on the permanent-loss acknowledgement
 *  (COB-272). Hook-free apart from local checkbox state, so it render-tests
 *  without a session provider. */
export function SeedBackupStep({
  seedWords,
  onContinue,
  notice,
}: {
  seedWords: string[];
  onContinue: () => void;
  /** COB-501: set when the verify step's attempt cap bounced the user back
   *  here, so the return isn't unexplained. */
  notice?: string | null;
}) {
  const [lossAcknowledged, setLossAcknowledged] = useState(false);
  return (
    <div class="home-auth home-auth--wide">
      <h2 class="home-auth-title">Back up your seed phrase</h2>
      <p class="home-auth-subtitle">
        Write down these 24 words in order. This seed controls all your vault
        keys. It is the only way to recover your funds on a new device.
      </p>
      {notice && <p class="home-auth-error">{notice}</p>}
      <div class="seed-warning">
        Never share your seed phrase. Anyone with these words can access your
        funds. Cobault staff will never ask for your seed phrase.
      </div>
      <div class="seed-grid">
        {seedWords.map((word, i) => (
          <div key={i} class="seed-word">
            <span class="seed-word-num">{i + 1}</span>
            <span class="seed-word-text">{word}</span>
          </div>
        ))}
      </div>
      <label class="loss-ack-row">
        <input
          type="checkbox"
          checked={lossAcknowledged}
          onChange={(e) =>
            setLossAcknowledged((e.target as HTMLInputElement).checked)
          }
        />
        <span>{SEED_LOSS_ACK_LABEL}</span>
      </label>
      <button
        class="btn btn-primary btn-block"
        onClick={onContinue}
        disabled={!lossAcknowledged}
      >
        I've written them down
      </button>
    </div>
  );
}

function SeedSetupBlocking({
  email,
  passcode,
  onDone,
  onBack,
}: {
  email: string;
  passcode: string;
  onDone: () => void;
  onBack: () => void;
}) {
  const session = useSession();
  const [step, setStep] = useState<SeedStep>('generating');
  const [seedWords, setSeedWords] = useState<string[]>([]);
  const [verifyIndices, setVerifyIndices] = useState<number[]>([]);
  const [verifyInputs, setVerifyInputs] = useState<string[]>(
    Array(VERIFY_COUNT).fill(''),
  );
  // COB-501: wrong attempts inside the CURRENT verification round. Reset on
  // every entry into the verify step, so leaving and returning is not a way to
  // farm attempts against one fixed set of positions — the positions are
  // re-drawn on that path too.
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [backupNotice, setBackupNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initStartedRef = useRef(false);

  // Kick off seed generation exactly once when the component mounts.
  if (!initStartedRef.current) {
    initStartedRef.current = true;
    session
      .beginNewAccount(email, passcode)
      .then((mnemonic) => {
        setSeedWords(mnemonic.split(' '));
        setStep('backup');
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to start setup');
      });
  }

  /** COB-501: draw an independent set of positions and clear the inputs. Called
   *  on entry into the verify step AND after every wrong attempt, so no two
   *  attempts are ever against the same three slots. */
  function rollVerifyChallenge() {
    setVerifyIndices(pickIndices(VERIFY_COUNT, seedWords.length));
    setVerifyInputs(Array(VERIFY_COUNT).fill(''));
  }

  function handleBackupContinue() {
    rollVerifyChallenge();
    setAttemptsUsed(0);
    setBackupNotice(null);
    setError(null);
    setStep('verify');
  }

  async function handleVerify() {
    setError(null);
    const correct = verifyIndices.every(
      (wordIdx, i) => verifyInputs[i].trim().toLowerCase() === seedWords[wordIdx],
    );
    if (!correct) {
      const used = attemptsUsed + 1;
      setAttemptsUsed(used);
      if (used >= MAX_VERIFY_ATTEMPTS) {
        // COB-501: out of attempts — back to the words themselves rather than
        // an unbounded guessing loop. The next entry re-rolls and resets the
        // counter, and the COB-272 acknowledgement has to be re-ticked.
        setVerifyInputs(Array(VERIFY_COUNT).fill(''));
        setBackupNotice(VERIFY_EXHAUSTED_NOTICE);
        setStep('backup');
        return;
      }
      rollVerifyChallenge();
      setError(verifyFailureMessage(MAX_VERIFY_ATTEMPTS - used));
      return;
    }
    setStep('registering');
    try {
      await session.completeNewAccount(email, passcode);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Registration failed');
      setStep('verify');
    }
  }

  function updateVerifyInput(i: number, value: string) {
    const next = [...verifyInputs];
    next[i] = value;
    setVerifyInputs(next);
  }

  if (step === 'generating') {
    return (
      <div class="home-auth">
        <p class="home-auth-subtitle">Generating your master seed...</p>
        {error && (
          <>
            <p class="home-auth-error">{error}</p>
            <button class="btn btn-secondary btn-block" onClick={onBack}>
              Back
            </button>
          </>
        )}
      </div>
    );
  }

  if (step === 'backup') {
    return (
      <SeedBackupStep
        seedWords={seedWords}
        onContinue={handleBackupContinue}
        notice={backupNotice}
      />
    );
  }

  if (step === 'registering') {
    return (
      <div class="home-auth">
        <p class="home-auth-subtitle">Creating your account...</p>
      </div>
    );
  }

  return (
    <div class="home-auth home-auth--wide">
      <h2 class="home-auth-title">Verify your backup</h2>
      <p class="home-auth-subtitle">
        Enter the requested words to confirm you've saved your seed correctly.
      </p>
      <div class="verify-fields">
        {verifyIndices.map((wordIdx, i) => (
          <div key={wordIdx} class="verify-field">
            <label>Word #{wordIdx + 1}</label>
            <input
              type="text"
              value={verifyInputs[i]}
              onInput={(e) =>
                updateVerifyInput(i, (e.target as HTMLInputElement).value)
              }
              autocomplete="off"
              autocapitalize="off"
            />
          </div>
        ))}
      </div>
      {error && <p class="home-auth-error">{error}</p>}
      <div class="form-actions">
        <button
          class="btn btn-secondary btn-block"
          onClick={() => setStep('backup')}
        >
          Back
        </button>
        <button
          class="btn btn-primary btn-block"
          onClick={handleVerify}
          disabled={verifyInputs.some((v) => !v.trim())}
        >
          Verify & continue
        </button>
      </div>
    </div>
  );
}

// ── Unlock runner (match + local seed) ──────────────────────────────

function UnlockRunner({
  email,
  passcode,
  onUnlockFailedUnknownUser,
  onError,
}: {
  email: string;
  passcode: string;
  onUnlockFailedUnknownUser: () => void;
  onError: (msg: string) => void;
}) {
  const session = useSession();
  const startedRef = useRef(false);
  if (!startedRef.current) {
    startedRef.current = true;
    session
      .unlockAndVerify(email, passcode)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          onUnlockFailedUnknownUser();
        } else {
          onError(e instanceof Error ? e.message : 'Unlock failed');
        }
      });
  }
  return (
    <div class="home-auth">
      <p class="home-auth-subtitle">Unlocking...</p>
    </div>
  );
}

// ── Restore prompt (match + no local seed) ──────────────────────────

function SeedEntryGrid({
  onSubmit,
  onBack,
  title,
  subtitle,
  submitLabel,
}: {
  onSubmit: (mnemonic: string) => void;
  onBack: () => void;
  title: string;
  subtitle: string;
  submitLabel: string;
}) {
  const [words, setWords] = useState<string[]>(Array(WORD_COUNT).fill(''));
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justPasted, setJustPasted] = useState(false);
  const filledCount = words.filter((w) => w.trim().length > 0).length;
  const allFilled = filledCount === WORD_COUNT;

  function updateWord(i: number, value: string) {
    const next = [...words];
    next[i] = value.toLowerCase().replace(/[^a-z]/g, '');
    setWords(next);
  }

  function handlePaste(e: ClipboardEvent) {
    const text = e.clipboardData?.getData('text') ?? '';
    const pasted = text.trim().toLowerCase().split(/\s+/);
    if (pasted.length >= 2) {
      e.preventDefault();
      const next = [...words];
      for (let i = 0; i < Math.min(pasted.length, WORD_COUNT); i++) {
        next[i] = pasted[i].replace(/[^a-z]/g, '');
      }
      setWords(next);
      setJustPasted(true);
      window.setTimeout(() => setJustPasted(false), 1200);
    }
  }

  function handleSubmit() {
    if (!allFilled) {
      setError('All 24 words are required');
      return;
    }
    const mnemonic = words.map((w) => w.trim().toLowerCase()).join(' ');
    onSubmit(mnemonic);
  }

  return (
    <div class="home-auth home-auth--wider">
      <h2 class="home-auth-title">{title}</h2>
      <p class="home-auth-subtitle">{subtitle}</p>
      <div class="home-auth-field">
        <label class="home-auth-label">
          Seed phrase ({filledCount}/{WORD_COUNT})
        </label>
      </div>
      <div
        class={`seed-entry-grid${justPasted ? ' seed-entry-grid--just-pasted' : ''}`}
        onPaste={handlePaste}
      >
        {words.map((word, i) => (
          <div key={i} class="seed-entry-cell">
            <span class="seed-entry-num">{i + 1}</span>
            <input
              class={`seed-entry-input${word.trim() ? ' seed-entry-filled' : ''}`}
              type={focusedIndex === i ? 'text' : 'password'}
              value={word}
              onInput={(e) => updateWord(i, (e.target as HTMLInputElement).value)}
              onFocus={() => setFocusedIndex(i)}
              onBlur={() => setFocusedIndex(null)}
              autocomplete="off"
              autocapitalize="off"
              autocorrect="off"
              spellcheck={false}
            />
          </div>
        ))}
      </div>
      <p class="seed-entry-hint">
        {justPasted
          ? 'Pasted! All words filled from your clipboard.'
          : 'Paste your entire phrase into any cell, or enter each word by hand.'}
      </p>
      {error && <p class="home-auth-error">{error}</p>}
      <div class="form-actions">
        <button class="btn btn-secondary btn-block" onClick={onBack}>
          Back
        </button>
        <button class="btn btn-primary btn-block" onClick={handleSubmit} disabled={!allFilled}>
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function RestorePrompt({
  email,
  onSubmitSeed,
  onBack,
}: {
  email: string;
  onSubmitSeed: (mnemonic: string) => void;
  onBack: () => void;
}) {
  return (
    <SeedEntryGrid
      title="Restore this device"
      subtitle={`Welcome back — we found your account for ${email}. Enter your 24-word seed phrase to restore access on this device.`}
      submitLabel="Restore"
      onSubmit={onSubmitSeed}
      onBack={onBack}
    />
  );
}

// ── Restore retry (wrong seed) ──────────────────────────────────────

/** The "that seed phrase isn't for this account" panel, plus the retry.
 *
 *  COB-646: the retry used to write `{ kind: 'restorePrompt' }` straight into
 *  the flow state — a transition `reduceAuthFlow` has no arm for, which left
 *  its real arm (`restoreWrongSeed + SubmitMnemonic → restoring`) dead in
 *  production. Showing the grid is a view step, so it is local boolean state
 *  here and the flow stays in `restoreWrongSeed` until the user submits, which
 *  takes that arm. The rendered panels are unchanged: the same explanation,
 *  then the same `<RestorePrompt />` grid. */
function RestoreWrongSeed({
  email,
  onRetrySeed,
  onBack,
}: {
  email: string;
  onRetrySeed: (mnemonic: string) => void;
  onBack: () => void;
}) {
  const [retrying, setRetrying] = useState(false);

  if (retrying) {
    return (
      <RestorePrompt email={email} onSubmitSeed={onRetrySeed} onBack={onBack} />
    );
  }

  return (
    <div class="home-auth home-auth--wide">
      <h2 class="home-auth-title">Seed phrase doesn't match</h2>
      <p class="home-auth-subtitle">
        The seed phrase you entered doesn't belong to the account for{' '}
        <strong>{email}</strong>. Try again with the correct seed phrase, or go
        back and use a different email.
      </p>
      <div class="form-actions-inline">
        <button class="btn btn-secondary btn-block" onClick={onBack}>
          Back
        </button>
        <button class="btn btn-primary btn-block" onClick={() => setRetrying(true)}>
          Try again
        </button>
      </div>
    </div>
  );
}

// ── Mismatch options ────────────────────────────────────────────────

function MismatchOptions({
  email,
  onRetry,
  onRecover,
}: {
  email: string;
  onRetry: () => void;
  onRecover: () => void;
}) {
  return (
    <div class="home-auth home-auth--wide">
      <h2 class="home-auth-title">Passcode doesn't match</h2>
      <p class="home-auth-subtitle">
        An account exists for <strong>{email}</strong>, but that passcode isn't
        right. You can try again, or recover with your seed phrase and set a new
        passcode.
      </p>
      <div class="home-auth-choices">
        <button class="home-auth-choice" onClick={onRetry}>
          <span class="home-auth-choice-title">Try a different passcode</span>
          <span class="home-auth-choice-desc">Go back and re-enter</span>
        </button>
        <button class="home-auth-choice" onClick={onRecover}>
          <span class="home-auth-choice-title">Recover with seed phrase</span>
          <span class="home-auth-choice-desc">
            Enter your 24-word backup and pick a new passcode
          </span>
        </button>
      </div>
    </div>
  );
}

// ── Recover with seed + new passcode ────────────────────────────────

function RecoverWithSeed({
  email,
  onSubmit,
  onBack,
}: {
  email: string;
  onSubmit: (mnemonic: string, newPasscode: string) => void;
  onBack: () => void;
}) {
  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const [newPasscode, setNewPasscode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (mnemonic === null) {
    return (
      <SeedEntryGrid
        title="Recover your account"
        subtitle={`Enter your 24-word seed phrase for ${email}. Next you'll set a new passcode.`}
        submitLabel="Continue"
        onSubmit={setMnemonic}
        onBack={onBack}
      />
    );
  }

  function handleReset() {
    setError(null);
    const pcErr = validatePasscode(newPasscode);
    if (pcErr) {
      setError(pcErr);
      return;
    }
    if (newPasscode !== confirm) {
      setError('Passcodes do not match');
      return;
    }
    onSubmit(mnemonic!, newPasscode);
  }

  return (
    // COB-502: a passcode *change* — the keychain update path. Same shape as
    // the create step: real form, one username companion for both fields.
    <form
      class="home-auth"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleReset();
      }}
    >
      <h2 class="home-auth-title">Set a new passcode</h2>
      <p class="home-auth-subtitle">
        Choose a new passcode for <strong>{email}</strong>. This replaces the
        forgotten one; your seed phrase and vaults are unchanged.
      </p>
      <PasswordManagerUsername id="cobault-recover-username" email={email} />
      <div class="home-auth-field">
        <label class="home-auth-label" for="cobault-recover-passcode">
          New passcode
        </label>
        <PasscodeInput
          id="cobault-recover-passcode"
          name="new-passcode"
          value={newPasscode}
          onInput={setNewPasscode}
          autocomplete="new-password"
          passwordrules={PASSCODE_RULES}
        />
      </div>
      <div class="home-auth-field">
        <label class="home-auth-label" for="cobault-recover-passcode-confirm">
          Confirm new passcode
        </label>
        <PasscodeInput
          id="cobault-recover-passcode-confirm"
          name="new-passcode-confirm"
          value={confirm}
          onInput={setConfirm}
          autocomplete="new-password"
          passwordrules={PASSCODE_RULES}
        />
      </div>
      {error && <p class="home-auth-error">{error}</p>}
      <div class="form-actions">
        <button type="button" class="btn btn-secondary btn-block" onClick={onBack}>
          Back
        </button>
        <button
          type="submit"
          class="btn btn-primary btn-block"
          disabled={!newPasscode || !confirm}
        >
          Reset passcode
        </button>
      </div>
    </form>
  );
}
